const monthMap: Record<string, number> = {
  // English
  Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
  Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11,
  // Spanish short (Feb, Mar, May, Jun, Jul, Sep, Oct y Nov ya están arriba)
  Ene: 0, Abr: 3,
  Ago: 7, Dic: 11,
  // Spanish full
  Enero: 0, Febrero: 1, Marzo: 2, Abril: 3, Mayo: 4, Junio: 5,
  Julio: 6, Agosto: 7, Septiembre: 8, Octubre: 9, Noviembre: 10, Diciembre: 11,
  // Portuguese-ish variants sometimes used
  Set: 8,
};

/** Try to parse a human-readable date string (e.g. "19 May 2026" or "2026-05-19") into a timestamp */
export function parseDateToTimestamp(dateStr: string): number {
  if (!dateStr || typeof dateStr !== "string") return 0;
  const trimmed = dateStr.trim();

  // ISO format: 2026-05-19
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const [y, m, d] = trimmed.split("-").map(Number);
    const ts = new Date(y, m - 1, d).getTime();
    return isNaN(ts) ? 0 : ts;
  }

  // ISO with time: 2026-05-19T12:00:00
  if (/^\d{4}-\d{2}-\d{2}T/.test(trimmed)) {
    const ts = new Date(trimmed).getTime();
    return isNaN(ts) ? 0 : ts;
  }

  // Human format: "19 May 2026" or "19 Mayo 2026"
  const parts = trimmed.split(/\s+/);
  if (parts.length >= 3) {
    const day = parseInt(parts[0], 10);
    const month = monthMap[parts[1]] ?? monthMap[parts[1].replace(".", "")];
    const year = parseInt(parts[parts.length - 1], 10);
    if (!isNaN(day) && month !== undefined && !isNaN(year)) {
      const ts = new Date(year, month, day).getTime();
      return isNaN(ts) ? 0 : ts;
    }
  }

  // Fallback: try native Date parsing
  const ts = new Date(trimmed).getTime();
  return isNaN(ts) ? 0 : ts;
}

/** Returns true if the given date string represents TODAY in the local timezone */
export function isToday(dateStr: string): boolean {
  const ts = parseDateToTimestamp(dateStr);
  if (!ts) return false;

  const d = new Date(ts);
  const now = new Date();

  return (
    d.getDate() === now.getDate() &&
    d.getMonth() === now.getMonth() &&
    d.getFullYear() === now.getFullYear()
  );
}

/** Returns true if the given date is within the last N days (default 3) */
export function isRecent(dateStr: string, days = 3): boolean {
  const ts = parseDateToTimestamp(dateStr);
  if (!ts) return false;

  const now = new Date();
  const cutoff = new Date(now.getFullYear(), now.getMonth(), now.getDate() - days);
  return ts >= cutoff.getTime();
}

/** Format an ISO date (2026-05-19) to display format (19 May 2026) */
export function formatDateForDisplay(isoDate: string): string {
  if (!isoDate) return "";
  const date = new Date(isoDate + "T12:00:00");
  if (isNaN(date.getTime())) return isoDate;
  const months = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
  return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
}

/** Format ISO date to input value (2026-05-19) from display format */
export function formatDateForInput(dateStr: string): string {
  if (!dateStr) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;

  const monthMapShort: Record<string, string> = {
    Ene: "01", Feb: "02", Mar: "03", Abr: "04", May: "05", Jun: "06",
    Jul: "07", Ago: "08", Sep: "09", Oct: "10", Nov: "11", Dic: "12",
    // Inglés (los demás meses se escriben igual que en español)
    Jan: "01", Apr: "04",
    Aug: "08", Dec: "12",
  };

  const parts = dateStr.split(" ");
  if (parts.length === 3) {
    const [day, month, year] = parts;
    const m = monthMapShort[month];
    if (m && year && day) {
      return `${year}-${m}-${day.padStart(2, "0")}`;
    }
  }
  return dateStr;
}